import React from 'react';
import {QueryClient, QueryClientProvider} from 'react-query';
import {
  ObservabilityProvider,
  useObservabilityConfig,
} from '@sail/observability';
import type {ObservabilityConfig} from '@sail/observability';
import type {
  OverlayOption,
  AppearanceWithDefaults,
} from '@stripe-internal/embedded-theming';
import {FetchContextProvider} from './fetch/FetchContext';
import type {IServiceEnvironmentContext} from './environment/ServiceEnvironmentContext';
import {ServiceEnvironmentContext} from './environment/ServiceEnvironmentContext';
import type {IMerchantDataContext} from './merchant/MerchantDataContext';
import {MerchantDataContext} from './merchant/MerchantDataContext';
import {OverlayOptionContext} from './overlayOption/OverlayOptionContext';
import type {IThemingContext} from './theming/ThemingContext';
import {ThemingContext} from './theming/ThemingContext';
import {OverlayProviderDoNotUse as OverlayProvider} from './overlay/OverlayContext';
import {InternalDomainContext} from './internalDomain/InternalDomainContext';
import {CustomFontsContext} from './customFonts/CustomFontsContext';
import type {CustomFontOptions} from '../ConnectJSInterface';
import {ViewportProvider} from '../utils/Responsive';
import type {ILayerContext} from './layer/LayerContext';
import {LayerContext} from './layer/LayerContext';
import {AppearanceWithDefaultsContext} from './theming/AppearanceWithDefaultsContext';

export type ConnectEmbeddedLibWrapperProps = {
  fetchFn?: typeof fetch;
  merchantData?: IMerchantDataContext;
  reactQueryClient?: QueryClient;
  serviceEnvironment: IServiceEnvironmentContext;
  themingContext?: IThemingContext;
  appearanceWithDefaults?: AppearanceWithDefaults;
  overlayOption?: OverlayOption;
  isInternalDomain: boolean;
  customFonts: CustomFontOptions;
  layerContext?: ILayerContext;
};

const DEFAULT_OBSERVABILITY_CONFIG: ObservabilityConfig = {
  project: 'accounts_ui',
  service: 'connect-js',
  errors: {
    projects: {
      accounts_ui:
        'https://6a9b51946de84c52b5c0c5ad058230a3@errors.stripe.com/1014',
    },
  },
};

/**
 * Sets the necessary context for UI in the connect embedded lib library to work. Users of the library are
 * expected to wrap components in this wrapper.
 */
export const ConnectEmbeddedLibWrapper: React.FC<
  ConnectEmbeddedLibWrapperProps
> = ({
  fetchFn = fetch,
  merchantData,
  reactQueryClient = new QueryClient(),
  serviceEnvironment,
  themingContext,
  appearanceWithDefaults,
  overlayOption,
  isInternalDomain,
  children,
  customFonts,
  layerContext,
}) => {
  // If the env where the library is used has not configured observability,
  // use a default configuration.
  const config = useObservabilityConfig() ?? DEFAULT_OBSERVABILITY_CONFIG;

  return (
    <ObservabilityProvider {...config}>
      <ServiceEnvironmentContext.Provider value={serviceEnvironment}>
        <MerchantDataContext.Provider value={merchantData}>
          <QueryClientProvider client={reactQueryClient}>
            <FetchContextProvider fetchOverride={fetchFn}>
              <LayerContext.Provider value={layerContext}>
                <OverlayOptionContext.Provider value={overlayOption}>
                  <ThemingContext.Provider value={themingContext}>
                    <AppearanceWithDefaultsContext.Provider
                      value={appearanceWithDefaults}
                    >
                      <InternalDomainContext.Provider value={isInternalDomain}>
                        <CustomFontsContext.Provider value={customFonts}>
                          <ViewportProvider>
                            <OverlayProvider>{children}</OverlayProvider>
                          </ViewportProvider>
                        </CustomFontsContext.Provider>
                      </InternalDomainContext.Provider>
                    </AppearanceWithDefaultsContext.Provider>
                  </ThemingContext.Provider>
                </OverlayOptionContext.Provider>
              </LayerContext.Provider>
            </FetchContextProvider>
          </QueryClientProvider>
        </MerchantDataContext.Provider>
      </ServiceEnvironmentContext.Provider>
    </ObservabilityProvider>
  );
};
